import i18n from '../../../i18n';
import type { GrowthChannelGroupId } from './growthTypes';

export type ChannelKind = 'online' | 'offline' | 'walkIn';

export type ChannelGroupMeta = {
  id: GrowthChannelGroupId;
  label: string;
  kind: ChannelKind;
  /** How the group is recognised, shown on hover so the grouping is auditable. */
  rule: string;
};

type ChannelGroupDef = { id: GrowthChannelGroupId; kind: ChannelKind };

/**
 * Marketing channel groups, in the order a marketer scans them. The grouping
 * itself happens on the server, from one rule list (growth_channel_rules.py,
 * WP3.6): GA4's default channel grouping plus Reserve with Google, Newsletter,
 * Influencer code and the other restaurant additions. Ids here must match it.
 * Labels and the rule shown on hover come from the Growth catalog
 * (`growth:channelGroups.<id>`), in the person's language (WP4.1).
 */
const CHANNEL_GROUP_DEFS: ChannelGroupDef[] = [
  { id: 'paid_search', kind: 'online' },
  { id: 'paid_social', kind: 'online' },
  { id: 'paid_shopping', kind: 'online' },
  { id: 'paid_video', kind: 'online' },
  { id: 'display', kind: 'online' },
  { id: 'paid_other', kind: 'online' },
  { id: 'cross_network', kind: 'online' },
  { id: 'organic_search', kind: 'online' },
  { id: 'organic_social', kind: 'online' },
  { id: 'organic_video', kind: 'online' },
  { id: 'organic_shopping', kind: 'online' },
  { id: 'influencer', kind: 'online' },
  { id: 'newsletter', kind: 'online' },
  { id: 'email', kind: 'online' },
  { id: 'local_listings', kind: 'online' },
  { id: 'reserve_with_google', kind: 'online' },
  { id: 'own_website', kind: 'online' },
  { id: 'referral', kind: 'online' },
  { id: 'affiliates', kind: 'online' },
  { id: 'qr_code', kind: 'online' },
  { id: 'ai_search', kind: 'online' },
  { id: 'direct', kind: 'online' },
  { id: 'audio', kind: 'online' },
  { id: 'sms', kind: 'online' },
  { id: 'mobile_push', kind: 'online' },
  { id: 'unassigned', kind: 'online' },
  { id: 'untagged', kind: 'online' },
  { id: 'phone_staff', kind: 'offline' },
  { id: 'ai_phone', kind: 'offline' },
  { id: 'other', kind: 'offline' },
  { id: 'walk_in', kind: 'walkIn' },
];

function withCopy(def: ChannelGroupDef): ChannelGroupMeta {
  return {
    ...def,
    label: i18n.t(`growth:channelGroups.${def.id}.label`),
    rule: i18n.t(`growth:channelGroups.${def.id}.rule`),
  };
}

/** Every channel group with its label and rule in the person's language. */
export function channelGroups(): ChannelGroupMeta[] {
  return CHANNEL_GROUP_DEFS.map(withCopy);
}

const BY_ID = new Map(CHANNEL_GROUP_DEFS.map(group => [group.id as string, group]));
const ORDER = new Map(CHANNEL_GROUP_DEFS.map((group, index) => [group.id as string, index]));

export function channelGroupMeta(id: string): ChannelGroupMeta {
  const def = BY_ID.get(id);
  return def ? withCopy(def) : { id: 'other', label: id, kind: 'offline', rule: '' };
}

export function channelGroupOrder(id: string): number {
  return ORDER.get(id) ?? CHANNEL_GROUP_DEFS.length;
}

/** Tagged means a marketer can say where the booking came from. */
export function isAttributable(id: string): boolean {
  return channelGroupMeta(id).kind === 'online' && id !== 'untagged';
}

/**
 * Chart fills: petrol tints by rank, untagged as a hatch so missing tracking
 * reads as a gap rather than a channel.
 */
const UNTAGGED_FILL = 'repeating-linear-gradient(135deg, var(--g-hatch) 0 3px, transparent 3px 7px)';
const OFFLINE_FILL = 'color-mix(in srgb, var(--ink) 22%, var(--surface))';
const PETROL_FILL = (pct: number) => `color-mix(in srgb, var(--petrol-ink) ${pct}%, var(--surface))`;
const PETROL_STEPS = [100, 76, 58, 44, 32, 22, 14];

export function channelFill(id: string, rank: number): string {
  if (id === 'untagged') return UNTAGGED_FILL;
  if (BY_ID.get(id)?.kind === 'offline') return OFFLINE_FILL;
  return PETROL_FILL(PETROL_STEPS[Math.min(rank, PETROL_STEPS.length - 1)]);
}

export function sourceMediumLabel(source: string | null, medium: string | null): string {
  const src = source && source !== '__none__' ? source : '';
  const med = medium || '';
  if (!src && !med) return i18n.t('growth:channelGroups.noTag');
  return `${src || i18n.t('growth:channelGroups.noSource')} / ${med || i18n.t('growth:channelGroups.noMedium')}`;
}
